import React, { useState, useEffect, useRef } from "react";
import InputField from "common/customInputField";
import Button from "react-bootstrap/Button";
import Modal from "common/modal";
import DataTable from "common/desktopDataTable";
import SelectInput from "common/selectInput";
import { useSelector, useDispatch } from "react-redux";
import { accountLookupExpandedItems, priceTypeOptions } from "utils/common";
import { accountLookupFetch } from "services/accountLookup.service";
import { updateSearchFields } from "store/laborTable/actionCreator";
import isNil from "lodash/isNil";
import "./style.scss";

export const LookupModal = ({ onClose, onSelection }) => {
  const { storeCcn } = useSelector((state) => state.loggedInUser);
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [storeNumber, setStoreNumber] = useState(storeCcn);
  const [lookupResults, setLookupResults] = useState([]);

  return (
    <Modal title="Account Lookup" handleCancel={onClose} size="lg" bodyClass="account-lookup-search-form">
      <div className="container-fluid search-form-container border-0">
        <div className="search-form-container__fields">
          <div className="row">
            <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
              <InputField
                type="text"
                label="Account Number"
                placeholder="Enter an account number"
                fieldClasses="form-control search-form-container__fields__form-group__item"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, "").substring(0, 9))}
              />
            </div>
            <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
              <InputField
                type="text"
                label="Account Name"
                placeholder="Enter an account name"
                fieldClasses="form-control search-form-container__fields__form-group__item"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
              <InputField
                type="text"
                label="Parent Store Number"
                placeholder="Enter a parent store number"
                fieldClasses="form-control search-form-container__fields__form-group__item"
                value={storeNumber}
                onChange={(e) => setStoreNumber(e.target.value)}
                errorMsg={storeNumber === "" ? "Please enter a Number" : ""}
              />
            </div>
          </div>
          <Button
            variant="outline-primary"
            disabled={isNil(storeNumber) || storeNumber === ""}
            onClick={() =>
              accountLookupFetch({ accountNumber, accountName, parentStoreCcn: storeNumber }).then(setLookupResults)
            }
          >
            Search
          </Button>
        </div>
      </div>
      {lookupResults.length > 0 && (
        <DataTable
          parentComponent="accountLookup"
          renderDesktopRows={(currentData) =>
            currentData.map((row, idx) => (
              <tr key={row.id ?? idx} className="table-root__body__tr" onClick={() => onSelection(row)}>
                <td className="table-root__body__tr__td table-root__body__tr__td__text customer-txt customer-txt_val">
                  {row.accountNumber}
                </td>
                <td className="table-root__body__tr__td table-root__body__tr__td__text customer-txt_val">
                  {row.accountName}
                </td>
              </tr>
            ))
          }
          tableHeader={[
            { id: 1, name: "Account Number", dataKey: "accountNumber", class: "accNumHeader", sortable: true },
            { id: 2, name: "Account Name", dataKey: "accountName", sortable: true },
          ]}
          tableBody={lookupResults}
          countTitle="Search Results"
          displaySearchFilter={false}
          isSelectAllChecked={false}
          expandedItems={accountLookupExpandedItems}
          actionsFormatter={onSelection}
        />
      )}
    </Modal>
  );
};

const AccountLookup = ({
  numberFieldChangeCallback,
  nameFieldChangeCallback,
  nameFieldWrapperClass = "col-12 col-sm-12 col-md-5 col-lg-5 search-form-container__fields__form-group",
  numberFieldWrapperClass = "col-12 col-sm-12 col-md-5 col-lg-5 search-form-container__fields__form-group",
  isEdit,
  store,
  handleJob,
  jobList,
  isExpPrice,
  tableSearchFields,
  initialAccountNumber = "",
  initialAccountName = "",
  isLaborSearch,
}) => {
  const dispatch = useDispatch();
  const priceTypeNode = useRef();
  const jobNode = useRef();

  const { exceptionPriceSearchFields } = useSelector((state) => state.laborTable);
  const [accountNumber, setAccountNumber] = useState(initialAccountNumber);
  const [accountNumberError, setAccountNumberError] = useState("");
  const [accountName, setAccountName] = useState(initialAccountName);
  const [showLookupModal, setShowLookupModal] = useState(false);

  useEffect(() => setAccountNumber(initialAccountNumber), [initialAccountNumber]);
  useEffect(() => setAccountName(initialAccountName), [initialAccountName]);
  useEffect(() => nameFieldChangeCallback(accountName, false, null), [accountName]);

  const selectAccount = (row) => {
    setAccountNumberError("");
    setShowLookupModal(false);
    setAccountNumber(row.accountNumber);
    setAccountName(row.accountName);
    nameFieldChangeCallback(row.accountName, true, row);
    numberFieldChangeCallback(row.accountNumber, true, row);
  };

  return (
    <>
      <div className="col-12 form-group account-info__container">
        {!isEdit && <h4 className="label_class account-info__header px-0">Account Information</h4>}
        <p className="account-info__instruction px-0">
          Use the account lookup button to get the required information from the database
        </p>
        <Button variant="outline-primary" className="mb-4" onClick={() => setShowLookupModal(true)}>
          Account Lookup
        </Button>
      </div>
      <form autoComplete="off">
        <div className="row">
          <div className={numberFieldWrapperClass}>
            <InputField
              type="text"
              inputMode="numeric"
              label={"Account Number"}
              id="accountNumber"
              placeholder="Enter an account number"
              fieldClasses="form-control search-form-container__fields__form-group__item"
              value={accountNumber}
              onChange={async (e) => {
                const accountNumber = e.target.value.replace(/\D/g, "").substring(0, 9);
                setAccountNumber(accountNumber);
                setAccountNumberError("");

                if (accountNumber.length === 9) {
                  const [account] = await accountLookupFetch({ accountNumber });
                  if (isNil(account)) {
                    setAccountNumberError("We couldn't find an account associated with this number");
                    numberFieldChangeCallback(accountNumber, false, account);
                  } else {
                    selectAccount(account);
                  }
                } else if (accountNumber.length === 0) {
                  numberFieldChangeCallback(accountNumber, true, null);
                } else {
                  numberFieldChangeCallback(accountNumber, false, null);
                }
              }}
              handleOnBlur={(e) => {
                if (![9, 0].includes(e.target.value.length)) {
                  setAccountNumberError("Account number should be 9 digits");
                }
              }}
              errorMsg={accountNumberError}
            />
          </div>
          <div className={nameFieldWrapperClass}>
            <InputField
              type="text"
              inputMode="numeric"
              label={"Account Name"}
              id="accountName"
              placeholder="Enter an account name"
              fieldClasses="form-control search-form-container__fields__form-group__item"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
            />
          </div>
          {isExpPrice && (
            <>
              <div className="col-12 col-sm-12 col-md-3 col-lg-3 search-form-container__fields__form-group">
                <SelectInput
                  inputRef={jobNode}
                  isJob={true}
                  options={jobList}
                  label="Job"
                  isNA={jobList.length ? false : true}
                  defaultValue={jobList?.length ? "Select a Job" : "None Available"}
                  value={
                    jobList?.length
                      ? exceptionPriceSearchFields.job?.id
                        ? exceptionPriceSearchFields.job?.id + " : " + exceptionPriceSearchFields.job?.description
                        : "Select a Job"
                      : "None Available"
                  }
                  handleSelectInput={(e, value) => handleJob(e, value)}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-3 col-lg-3 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label="Store Number"
                  id="storeNumber"
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={store ?? ""}
                  disabled={true}
                  isRequired={false}
                />
              </div>
            </>
          )}
          {isLaborSearch && (
            <>
              <div className="col-12 col-sm-12 col-md-3 col-lg-3 search-form-container__fields__form-group">
                <SelectInput
                  inputRef={jobNode}
                  isJob={true}
                  options={jobList}
                  label="Job"
                  isNA={jobList?.length ? false : true}
                  defaultValue={jobList?.length ? "Select a Job" : "None Available"}
                  value={
                    jobList?.length
                      ? tableSearchFields.job?.description === ""
                        ? tableSearchFields.job?.id
                        : tableSearchFields.job?.id + " : " + tableSearchFields.job?.description
                      : "None Available"
                  }
                  handleSelectInput={(e, value) => handleJob(e, value)}
                />
              </div>

              <div className="col-12 col-sm-12 col-md-6 col-lg-2 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label="National Parent"
                  id="parentNumber"
                  placeholder="Enter a number"
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={tableSearchFields.parentNumber ?? ""}
                  onChange={(e) => dispatch(updateSearchFields("parentNumber", e.target.value))}
                  disabled={false}
                  isRequired={false}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-6 col-lg-2 search-form-container__fields__form-group">
                <SelectInput
                  inputRef={priceTypeNode}
                  options={priceTypeOptions}
                  label="Price Type"
                  defaultValue="Select Price Type"
                  value={tableSearchFields.priceType.description}
                  handleSelectInput={(e, value) => dispatch(updateSearchFields("priceType", value))}
                />
              </div>
            </>
          )}
        </div>
      </form>
      {showLookupModal && <LookupModal onClose={() => setShowLookupModal(false)} onSelection={selectAccount} />}
    </>
  );
};

export default AccountLookup;
